import { useTranslation, Trans } from "i18n";
import React, { FC } from "react";
import { InfoCircleIcon as InfoIconSvg } from "assets";
import { IPrice, ICouponObject } from "../../types";
import OrderSummary from "../OrderSummary/OrderSummary";
import StatusMessage from "../../../../Atoms/StatusMessage";

interface Props {
  planName: string;
  price: IPrice | undefined;
  couponBag: {
    userCouponCodesObject: ICouponObject[];
    setCoupon: (couponCode: string) => Promise<void>;
    removeCoupon: (couponCode: string) => Promise<void>;
    couponErrors: string[];
    hasAccountDiscount?: boolean;
    isCheckoutPreviewRequestLoading: boolean;
  };
  isMobileView?: boolean;
  planNameTooltip?: {
    header: string;
    body: string;
  };
  nextBillingDate?: {
    date: string;
    price: string;
  };
  disclaimerMessage?: string;
  countryCode?: string;
  customTexts?: Record<string, string>;
}

const SummaryBlock: FC<React.PropsWithChildren<Props>> = ({
  planName,
  price,
  couponBag,
  isMobileView,
  planNameTooltip,
  nextBillingDate,
  disclaimerMessage,
  countryCode,
  customTexts,
}) => {
  const { t } = useTranslation();
  const classNames = isMobileView ? "m-2" : "mt-[2rem] w-full max-w-[450px]";
  const vatLabel =
    countryCode && !couponBag.isCheckoutPreviewRequestLoading
      ? t("billing.checkout.orderSummary.label.vatDetail", {
          percent: (price?.taxPercentage || 0) * 100,
          countryCode,
        })
      : t("common:billing.checkout.orderSummary.label.vat");
  const vatAmount = countryCode
    ? price?.tax
    : t("common:billing.checkout.orderSummary.loadingPrice");

  return (
    <aside className={`sticky ${classNames}`}>
      <OrderSummary
        planName={planName}
        total={countryCode ? price?.total : "-"}
        amount={price?.amount}
        discountAmount={price?.discount}
        creditApplied={price?.creditApplied}
        accountCredited={price?.accountCredited}
        vatAmount={vatAmount}
        vatLabel={vatLabel}
        userCouponCodesObject={couponBag.userCouponCodesObject}
        setCoupon={couponBag.setCoupon}
        removeCoupon={couponBag.removeCoupon}
        couponErrors={couponBag.couponErrors}
        hasAccountDiscount={couponBag.hasAccountDiscount}
        isMobileView={isMobileView}
        isCheckoutPreviewRequestLoading={
          couponBag.isCheckoutPreviewRequestLoading
        }
        addOns={price?.addOns}
        planNameTooltip={planNameTooltip}
        nextBillingDate={nextBillingDate}
        disclaimerMessage={disclaimerMessage}
        renderSubtotalBlock={() => (
          <div>
            <div className="mt-2 text-end">
              {t("common:billing.checkout.orderSummary.subtotal", {
                subtotal: couponBag.isCheckoutPreviewRequestLoading
                  ? t("common:billing.checkout.orderSummary.loadingPrice")
                  : price?.subtotal,
              })}
            </div>
            {customTexts?.planEndDateMessage && (
              <StatusMessage className="text-black">
                <Trans i18nKey={customTexts.planEndDateMessage} />
              </StatusMessage>
            )}
            {!countryCode && (
              <div className="flex gap-3 p-2.5 mt-4 rounded bg-color-neutral-four text-color-neutral-one font-semibold">
                <div>
                  <InfoIconSvg width={25} height={25} />
                </div>
                {t("common:billing.checkout.orderSummary.vatWarning")}
              </div>
            )}
          </div>
        )}
      />
    </aside>
  );
};

export default SummaryBlock;
